/* ============================================================
   dp-gift-with-purchase.js — auto-add a free Gift-with-Purchase
   when the cart qualifies for the GWP automatic discount.

   Single source of truth = the Shopify automatic Buy-X-Get-Y
   discount. This module only ADDS the configured gift variant and
   KEEPS it while Shopify has actually zeroed that line
   (final_line_price === 0); otherwise it removes it. It never
   decides "does the cart qualify?" itself — Shopify does, and the
   $0 line price is the signal.

   Coexistence with the product-options configurator: the gift add
   carries a `_po_external` line property, which the configurator's
   interceptor passes straight through (product-options.js ->
   bodyHasExternalFlag). Removal uses /cart/update.js, which the
   configurator does not gate.
   ============================================================ */
(function () {
  'use strict';

  const GIFT_PROP = '_gwp';             // marks the gift line
  const EXTERNAL_PROP = '_po_external'; // tells the configurator to ignore this add
  const RECONCILE_DELAY = 250;

  const readConfig = () => {
    const el = document.getElementById('dp-gwp-config');
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent || '{}');
      return {
        enabled: !!cfg.enabled,
        giftVariantId: cfg.giftVariantId ? Number(cfg.giftVariantId) : null,
        threshold: cfg.threshold ? Number(cfg.threshold) : 0, // cents; nudge + cheap pre-filter
        label: typeof cfg.label === 'string' ? cfg.label : '' // merchant-set gift badge text
      };
    } catch (err) {
      console.error('[dp-gwp] bad config JSON', err);
      return null;
    }
  };

  window.DP.register('gift-with-purchase', function () {
    const DP = window.DP;

    // Never churn the cart inside the theme editor.
    if (DP.isDesignMode) return;
    // Cart mutations are sensitive — register exactly once even if init re-runs.
    if (DP._gwpInit) return;
    DP._gwpInit = true;

    const cfg = readConfig();
    if (!cfg) return; // config snippet not rendered → feature absent

    let inFlight = false;  // re-entrancy guard around our own add/remove
    let exhausted = false; // stop re-attempting an add that will not go $0
    let lastCart = null;   // most recent cart snapshot (for the checkout guard)

    const findGift = (cart) =>
      (cart.items || []).find(i => i.properties && i.properties[GIFT_PROP]);

    // Spend used for the cheap pre-filter. Subtotal best mirrors a discount's
    // "minimum purchase"; the authoritative gate is always the $0 line below.
    const qualifyingTotal = (cart) =>
      (cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price) || 0;

    // Progress nudge — fills any [data-dp-gwp-nudge] element. Display only; the
    // discount is authoritative, so the copy stays soft (it can't know the
    // discount's exact collection scope).
    const updateNudge = (cart) => {
      const els = document.querySelectorAll('[data-dp-gwp-nudge]');
      if (!els.length) return;
      const gift = findGift(cart);
      const remaining = cfg.threshold - qualifyingTotal(cart);
      let msg = '';
      if (gift && gift.final_line_price === 0) {
        msg = '🎁 Free gift unlocked!';
      } else if (cfg.threshold > 0 && remaining > 0) {
        msg = 'Add ' + DP.formatMoney(remaining) + ' more for a free pre-soldered keychain 🎁';
      } else if (cfg.threshold > 0) {
        msg = '🎁 Free gift unlocked!';
      }
      els.forEach((el) => { el.textContent = msg; el.hidden = !msg; });
    };

    const addGift = () => {
      if (!cfg.giftVariantId) {
        console.warn('[dp-gwp] enabled but no gift variant configured');
        return Promise.resolve();
      }
      inFlight = true;
      return DP.cart
        .add({ id: cfg.giftVariantId, quantity: 1, properties: { [GIFT_PROP]: true, [EXTERNAL_PROP]: true } })
        .catch(err => console.error('[dp-gwp] gift add failed', err))
        .then(() => { inFlight = false; });
    };

    const removeGift = (gift) => {
      if (!gift) return Promise.resolve();
      inFlight = true;
      return DP.cart
        .update({ updates: { [gift.key]: 0 } }) // line key = surgical; survives a variant re-config
        .catch(err => console.error('[dp-gwp] gift remove failed', err))
        .then(() => { inFlight = false; });
    };

    const reconcile = (cart) => {
      lastCart = cart;
      const gift = findGift(cart);

      // Deactivated → pull any lingering gift from the cart, then stop.
      if (!cfg.enabled) return gift ? removeGift(gift) : Promise.resolve();

      updateNudge(cart);

      if (inFlight) return Promise.resolve();

      if (gift) {
        if (gift.final_line_price === 0) { exhausted = false; return Promise.resolve(); } // qualified & free → keep
        return removeGift(gift); // present but charged → no longer qualifies / discount off
      }

      // No gift in cart.
      if (qualifyingTotal(cart) < cfg.threshold) { exhausted = false; return Promise.resolve(); }
      if (exhausted) return Promise.resolve();

      return addGift()
        .then(() => DP.cart.get())
        .then((after) => {
          lastCart = after;
          const g2 = findGift(after);
          if (!(g2 && g2.final_line_price === 0)) {
            exhausted = true;     // discount not live / cart did not qualify → undo, don't loop
            return removeGift(g2);
          }
        });
    };

    const runReconcile = () =>
      DP.cart.get().then(reconcile).catch(err => console.error('[dp-gwp] reconcile failed', err));

    const debouncedReconcile = DP.debounce(runReconcile, RECONCILE_DELAY);

    // Deactivated → one-shot cleanup of any lingering gift, then stay dormant
    // (no ongoing listener, no per-update /cart.js cost while the feature is off).
    if (!cfg.enabled) { runReconcile(); return; }

    // Every cart mutation dispatches dp:cart:updated (DP.cart.* and the
    // configurator bridge both do). Coalesce bursts, then re-fetch /cart.js for
    // the authoritative discounted state. Idempotent reconcile + inFlight guard
    // make our own echo a harmless no-op (no add/remove loop).
    document.addEventListener('dp:cart:updated', debouncedReconcile);

    // Initial pass — covers a refresh that lands already-qualified.
    runReconcile();

    // --- Checkout-entry guard (charged-for-gift) ------------------------------
    // Reconcile self-heals a non-$0 gift on every cart change; this closes the
    // sub-debounce race if the shopper clicks checkout immediately after a change
    // that dropped them below threshold. Shopify also shows true line prices at
    // checkout review, so this is UX insurance, not a payment-safety gate. Express
    // buttons that POST the cart directly are not covered (documented limitation).
    let bypassGuard = false;
    document.addEventListener('click', (e) => {
      if (bypassGuard || !cfg.enabled) return;
      const btn = e.target.closest('button[name="checkout"], a[href*="/checkout"], [data-dp-checkout]');
      if (!btn) return;
      const stale = lastCart && findGift(lastCart);
      if (!stale || stale.final_line_price === 0) return; // gift absent or free → let it through
      e.preventDefault();
      e.stopPropagation();
      DP.cart.get()
        .then(cart => removeGift(findGift(cart)))
        .then(() => { bypassGuard = true; btn.click(); });
    }, true);
  });
})();
